/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import * as React from "react";
import { t } from "i18n";

import classnames from "classnames/bind";
import User from "components/icons/user";
import Clock from "components/icons/clock";
import Checkbox from "components/checkbox/checkbox";
import Chip from "components/chip/chip";
import Preview from "components/icons/preview";
import { formattedDate } from "utils";
import { ContextMenu, ContextMenuList, ContextMenuItem } from "components/context-menu/context-menu";
import { STORY_EDITOR_PATH_REFRESH } from "../../../story-editor/routes";

import styles from "./story-card.module.css";
import { getPublishedStory } from "api/story";
import Calendar from "components/icons/calendar";
import Tooltip from "components/tooltip/tooltip";

const getAuthorDelimiter = (numberOfAuthors, index) => {
  if (numberOfAuthors === 1) {
    return null;
  } else if (numberOfAuthors === 2) {
    return index === 0 && <span className={styles["author-delimiter-and"]}>&</span>;
  } else {
    return index === numberOfAuthors - 2 ? (
      <span className={styles["author-delimiter-and"]}>&</span>
    ) : (
      index !== numberOfAuthors - 1 && ","
    );
  }
};

const cx = classnames.bind(styles);

const Author = ({ author, delimiter, setStoriesOnFilterChange }) => (
  <div
    data-test-id="story-author-btn"
    className={styles["story-author"]}
    onClick={() => setStoriesOnFilterChange("authors", [{ id: author.id, name: author.name }])}>
    <span
      dangerouslySetInnerHTML={{
        __html: author["highlighted-name"] || author["name"]
      }}
    />
    {delimiter}
  </div>
);

const Authors = ({ story, setStoriesOnFilterChange }) => {
  const authorsList =
    story.authors && story.authors.length > 0
      ? story.authors
      : [{ id: story["author-id"], name: story["author-name"] }];

  return authorsList.map((author, index) => (
    <Author
      data-test-id="story-author"
      key={`story-author-${author.id}`}
      author={author}
      delimiter={getAuthorDelimiter(authorsList.length, index)}
      setStoriesOnFilterChange={setStoriesOnFilterChange}
    />
  ));
};

const getStoryTemplate = (storyTemplate, storyTemplates) =>
  storyTemplate
    ? [storyTemplates.find((template) => template.slug === storyTemplate)]
    : [{ name: "Text", slug: "text" }];

const getPublishedStoryUrl = async (storyId) => {
  try {
    const { story }: any = await getPublishedStory(storyId);
    return story && story.url;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const StoryCardMenu = ({ id, frontEndUrl, switchToDeleteStoryRoute, canDeleteStory, isStoryPublished }) => (
  <div id={`story-card-menu-${id}`} className={styles["story-card-menu"]} data-test-id="story-card-menu">
    <ContextMenuList>
      {isStoryPublished && (
        <ContextMenuItem
          label={t("workspace.story_card.go_to_story")}
          onClick={async () => {
            const url = await getPublishedStoryUrl(id);
            window.open(url || frontEndUrl, "_blank");
          }}
        />
      )}
      {canDeleteStory && (
        <ContextMenuItem label={t("workspace.story_card.delete")} onClick={() => switchToDeleteStoryRoute(id)} />
      )}
    </ContextMenuList>
  </div>
);

const StoryCard = ({
  story,
  showPublicPreviewLink,
  canDeleteStory,
  isBulkActionEnabled,
  checked,
  onChange,
  setStoriesOnFilterChange,
  switchToDeleteStoryRoute,
  isAllTabSelected,
  storyTemplates,
  navigate,
  features
}) => {
  const storyStatus = story["status"];
  const storyTemplate = story["story-template"] || "text";
  return (
    <div className={cx("story-card", "workspace-storycard")} data-test-id="workspace-story-card">
      {isBulkActionEnabled && (
        <div className={styles["story-multi-select"]} data-test-id="story-multi-select">
          <Checkbox
            checked={checked}
            id={`bulk-checkbox-${story.id}`}
            onChange={onChange}
            classname={"story-card-checkbox"}
          />
        </div>
      )}
      <div className={styles["story-card-content"]} data-test-id="story-card-content">
        <div className={styles["story-chips"]} data-test-id="story-chips">
          {isAllTabSelected && storyStatus && (
            <Chip value={t(`workspace.tabs.${storyStatus}`, storyStatus)} invert classname={"story-card-chip"} />
          )}
          <span
            className={styles["story-chip-wrapper"]}
            data-test-id="story-card-chip-wrapper-btn"
            onClick={() =>
              setStoriesOnFilterChange("story-templates", getStoryTemplate(story["story-template"], storyTemplates))
            }>
            <Chip value={t(`story-editor.story-template.${storyTemplate}`, storyTemplate)} />
          </span>
        </div>

        <a
          href={`${STORY_EDITOR_PATH_REFRESH}${story.id}`}
          className={styles["story-headline"]}
          data-test-id="story-headline"
          dangerouslySetInnerHTML={{
            __html: story["highlighted-headline"] || story.headline
          }}></a>

        <div className={styles["story-byline"]} data-test-id="story-byline">
          <div className={styles["story-authors"]} data-test-id="story-authors">
            <User />
            <Authors story={story} setStoriesOnFilterChange={setStoriesOnFilterChange} />
          </div>
          <div className={styles["story-time-container"]}>
            <div className={styles["story-time-wrapper"]} data-test-id="story-time-wrapper">
              <Clock />
              <time
                className={styles["story-time"]}
                data-test-id="story-time"
                data-for="published-or-updated-timer-tooltip"
                data-tip>
                {story.isStoryPublished
                  ? formattedDate(story["last-published-at"])
                  : formattedDate(story["updated-at"])}
                <Tooltip
                  id={"published-or-updated-timer-tooltip"}
                  place="bottom"
                  effect="solid"
                  value={t("common.last-updated")}
                  classname={styles["story-time-tooltip"]}
                />
              </time>
            </div>
            {story.status === "scheduled" && (
              <div className={styles["story-time-wrapper"]} data-test-id="story-time-wrapper">
                <Calendar width={16} height={16} />
                <time
                  className={styles["story-time"]}
                  data-test-id="story-time"
                  data-for="scheduled-timer-tooltip"
                  data-tip>
                  {!story["publish-at"]
                    ? t("workspace.story_card.publish_with_collection")
                    : formattedDate(story["publish-at"])}
                  <Tooltip
                    id="scheduled-timer-tooltip"
                    place="bottom"
                    effect="solid"
                    value={t("common.scheduled")}
                    classname={styles["story-time-tooltip"]}
                  />
                </time>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles["story-card-actions"]} data-test-id="story-card-actions">
        {/* <div className={styles["story-submit-button"]}>
          <Button type="secondary"> Submit </Button>
        </div> */}
        {showPublicPreviewLink && (
          <a
            data-test-id="public-preview-url-btn"
            href={story["public-preview-url"]}
            target="_blank"
            rel="noopener noreferrer"
            className={styles["story-preview"]}>
            <Preview />
          </a>
        )}
        {(story["is-published"] || canDeleteStory) && (
          <div
            id={`story-card-menu-${story.id}`}
            className={styles["story-more-actions"]}
            data-test-id="story-card-menu">
            <ContextMenu>
              <StoryCardMenu
                id={story.id}
                isStoryPublished={story["is-published"]}
                frontEndUrl={story["frontend-url"]}
                canDeleteStory={canDeleteStory}
                switchToDeleteStoryRoute={switchToDeleteStoryRoute}
              />
            </ContextMenu>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryCard;
